<template>
  <div>
    <div v-if="loading" class="dot-opacity-loader" style="z-index: 5">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div v-if="downtimeData && downtimeData.length" id="chartContainer" style="height: 150px"></div>
    <h4 v-else class="text-center p-3">{{ $t('dashboards.noData') }}</h4>
  </div>
</template>

<script>
import workCenterEvents from '@/api/boxinfo/workCenterEvents.gql';
import moment from 'moment';
import CanvasJS from '@/plugins/canvasjs.min';
const canvas = CanvasJS.Chart ? CanvasJS : window.CanvasJS;

export default {
  name: 'EventChart',
  props: {
    workCenterId: String,
    period: Object,
    stateId: String,
  },
  apollo: {
    downtimeData: {
      query: workCenterEvents,
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.period,
        };
      },
      pollInterval: 28000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        this.maxDate = this.period.dateEnd
          ? new Date(this.period.dateEnd).getTime()
          : new Date().getTime();
        return (queryData || {}).workCenterEventsList || [];
      },
      skip() {
        return !this.workCenterId;
      },

      fetchPolicy: 'cache-and-network',
    },
  },
  data() {
    return {
      downtimeData: [],
      selectionInner: null,
      selectionChangeCounter: 0,
      chart: null,
      maxDate: null,
      zoomMax: null,
      zoomMin: null,
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    chartOptionsCj() {
      const vm = this;
      return {
        theme: 'dark2',
        zoomEnabled: true,
        rangeChanging: vm.maxMinViewport,
        zoomType: 'y',
        backgroundColor: 'rgb(25, 28, 36)',
        dataPointWidth: 100,
        title: {
          text: !vm.downtimeData || !vm.downtimeData.length ? vm.$t('dashboards.noData') : null,
          fontSize: 20,
        },
        axisX: {
          tickLength: 0,
          margin: 10,
          lineThickness: 0,
          valueFormatString: ' ',
        },
        axisY: {
          minimum: vm.minDate,
          maximum: vm.maxDate,
          includeZero: false,
          lineThickness: 1,
          gridThickness: 0,
          lineColor: 'grey',
          labelFontColor: 'grey',
          tickColor: 'grey',
          labelMaxWidth: 40,
          interval: 14400000,
          labelFormatter: function (e) {
            const width = e.chart.width;
            const timestamp = e.axis.maximum - e.axis.minimum;
            const zoom =
              e.chart.sessionVariables.axisY[0].newViewportMaximum -
              e.chart.sessionVariables.axisY[0].newViewportMinimum;
            const interval = zoom ? vm.timeDiff(zoom, width) : vm.timeDiff(timestamp, width);
            e.chart.axisY[0].interval = interval;
            return moment(e.value).locale(vm.locale).format('DD MMM HH:mm');
          },
        },
        toolTip: {
          contentFormatter: function (e) {
            const name = e.entries[0].dataPoint.name;
            const color = e.entries[0].dataPoint.color;
            const start = moment(e.entries[0].dataPoint.y[0])
              .locale(vm.locale)
              .format('DD MMM HH:mm:ss');
            const end = moment(e.entries[0].dataPoint.y[1])
              .locale(vm.locale)
              .format('DD MMM HH:mm:ss');
            return `<b><font color=${color}>${name}</font></b>:<br>${start} - ${end}`;
          },
        },
        data: [
          {
            type: 'rangeBar',
            dataPoints: vm.dataReady,
          },
        ],
      };
    },
    minDate() {
      return this.period.dateStart
        ? new Date(this.period.dateStart).getTime()
        : new Date().getTime();
    },
    dataReady() {
      return this.prepData(this.downtimeData);
    },
    loading() {
      return (this.$apollo.queries.downtimeData || {}).loading;
    },
  },
  watch: {
    stateId() {
      if (this.chartOptionsCj) {
        this.chart = new canvas.Chart('chartContainer', this.chartOptionsCj);
        this.chart.render();
      }
    },
  },
  updated() {
    const chartId = 'chartContainer';
    if (document.getElementById(chartId)) {
      this.chartOptionsCj.axisY.viewportMinimum = this.zoomMin;
      this.chartOptionsCj.axisY.viewportMaximum = this.zoomMax;
      this.chart = new canvas.Chart(chartId, this.chartOptionsCj);
      this.chart.render();
    }
  },
  methods: {
    maxMinViewport(e) {
      this.zoomMax = e.axisY[0].viewportMaximum;
      this.zoomMin = e.axisY[0].viewportMinimum;
      if (e.trigger == 'zoom') {
        this.$emit('changeViewPort', this.zoomMin, this.zoomMax);
      }
      if (e.trigger == 'reset') {
        this.chartOptionsCj.axisY.viewportMinimum = null;
        this.chartOptionsCj.axisY.viewportMaximum = null;
        this.$emit('changeViewPort', 0, 0);
      }
    },
    timeDiff(timestamp, width) {
      const thirtyMinutes = 1800000;
      const fifteenMinutes = 900000;
      const day = 86400000;
      const days = timestamp / day;
      const daysRounded = Math.round(days);
      const intervalZoom =
        days < 0.2 ? this.isMobile(width, fifteenMinutes) : this.isMobile(width, thirtyMinutes);
      const interval =
        days < 0.5 ? intervalZoom : (daysRounded * day) / this.dayCount(daysRounded, width);
      return interval;
    },
    isMobile(width, time) {
      return width > 1281 ? time : width > 500 ? time * 2 : time * 4;
    },
    dayCount(days, width) {
      return width > 1281
        ? days <= 1
          ? 24
          : days < 24
          ? days
          : days < 48
          ? days / 2
          : days < 96
          ? days / 4
          : days < 192
          ? days / 8
          : days / 24
        : width > 500
        ? 12
        : 8;
    },
    prepData(values) {
      const darkColor = '#3b3c3d';
      const data = values.map((i) => {
        return {
          name: i.state.name,
          color: i.state.color
            ? this.stateId
              ? this.stateId === i.state.id
                ? i.state.color.trim()
                : darkColor
              : i.state.color.trim()
            : '#6D6E71',
          x: 1,
          y: [moment(i.eventStart).valueOf(), moment(i.eventEnd).valueOf()],
        };
      });
      return data;
    },
  },
};
</script>
